import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  LinearProgress,
  IconButton,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  ContentCopy,
  OpenInNew,
  Refresh,
  AssessmentOutlined,
  PlaylistPlay,
  VisibilityOutlined,
  VisibilityOffOutlined,
} from "@mui/icons-material";
import PageHeader from "../components/ui/PageHeader";
import { machineService } from "../services/api";
import { LaserMachine } from "../types";
import { useApp } from "../contexts/AppContext";
import { copyTextToClipboard, promptManualCopy } from "../utils/clipboard";
import { MACHINE_LICENSES, getLicenseInfo, formatLicenseDate } from "../utils/licenses";

const MachineDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state } = useApp();
  const [machine, setMachine] = useState<LaserMachine | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{
    severity: "success" | "info" | "warning" | "error";
    message: string;
  } | null>(null);

  const canManage = ["administrator", "reseller"].includes(
    state.auth.user?.role || "",
  );
  const backPath = "/machines";
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = ["overview", "licenses", "remote"].includes(
    searchParams.get("tab") || "",
  )
    ? searchParams.get("tab")!
    : "overview";
  const [showRemotePassword, setShowRemotePassword] = useState(false);

  const loadMachine = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await machineService.getMachine(id);
      setMachine(data);
    } catch (err: any) {
      setError(
        err?.response?.data?.error?.message ||
          "Errore nel caricamento dei dettagli macchina",
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadMachine();
  }, [loadMachine]);

  const [remoteConfig, setRemoteConfig] = useState<{
    idServer: string;
    relayServer: string;
    key: string;
  } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/rustdesk-config.json", {
      cache: "no-store",
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error("Configurazione non disponibile");
        return response.json();
      })
      .then((config) => {
        if (
          typeof config.idServer !== "string" ||
          !config.idServer ||
          typeof config.relayServer !== "string" ||
          !config.relayServer ||
          typeof config.key !== "string" ||
          !config.key
        )
          return;
        setRemoteConfig(config);
      })
      .catch(() => {
        /* Keep connection disabled without private server configuration. */
      });
    return () => controller.abort();
  }, []);

  const copyText = useCallback(async (text: string, label: string) => {
    if (!text) {
      setNotice({ severity: "warning", message: `${label} non disponibile` });
      return;
    }
    const copied = await copyTextToClipboard(text);
    if (copied) {
      setNotice({ severity: "success", message: `${label} copiato` });
      return;
    }
    promptManualCopy(text, label);
    setNotice({
      severity: "warning",
      message: `Clipboard non disponibile: copia manuale ${label.toLowerCase()}`,
    });
  }, []);

  const openRustDeskDesktop = useCallback(async () => {
    if (!remoteConfig) {
      setNotice({
        severity: "warning",
        message: "Configurazione del server privato non disponibile",
      });
      return;
    }
    if (!machine?.rustdeskId) {
      setNotice({
        severity: "warning",
        message: "RustDesk ID non disponibile per questa macchina",
      });
      return;
    }

    const connectionData = machine.rustdeskPassword
      ? `ID: ${machine.rustdeskId}\nPassword: ${machine.rustdeskPassword}`
      : `ID: ${machine.rustdeskId}`;
    const copied = await copyTextToClipboard(connectionData);

    const remoteId = encodeURIComponent(machine.rustdeskId);
    const password = machine.rustdeskPassword
      ? encodeURIComponent(machine.rustdeskPassword)
      : "";
    const deepLink = password
      ? `rustdesk://${remoteId}?password=${password}`
      : `rustdesk://${remoteId}`;

    window.location.href = deepLink;
    setNotice({
      severity: copied ? "info" : "warning",
      message: copied
        ? "Tentativo apertura RustDesk Desktop in corso. Se non compila la password automaticamente, incolla con Ctrl+V."
        : "Tentativo apertura RustDesk Desktop in corso. Inserisci ID e password manualmente se richiesto.",
    });
  }, [machine, remoteConfig]);

  const getStatusColor = (status?: string) => {
    const normalized = (status || "").toUpperCase();
    switch (normalized) {
      case "ONLINE":
        return "success";
      case "OFFLINE":
        return "default";
      case "MAINTENANCE":
        return "warning";
      case "ERROR":
        return "error";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !machine) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(backPath)}
          sx={{ mb: 2 }}
        >
          Indietro
        </Button>
        <Alert severity="error">{error || "Macchina non trovata"}</Alert>
      </Box>
    );
  }

  const installedHmiBuild =
    String(machine.softwareVersion || machine.firmwareVersion || "").trim() ||
    "-";

  const info = (label: string, value: React.ReactNode) => (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ mt: 0.5, fontWeight: 600, overflowWrap: "anywhere" }}
      >
        {value || "Non indicato"}
      </Typography>
    </Box>
  );
  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(backPath)}
        sx={{ mb: 2, px: 0 }}
      >
        Parco macchine
      </Button>
      <PageHeader
        title={machine.name}
        subtitle={`${machine.model} · Matricola ${machine.serialNumber}`}
        actions={
          <>
            <Button startIcon={<Refresh />} onClick={loadMachine}>
              Aggiorna
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssessmentOutlined />}
              onClick={() =>
                navigate(`/reports?machineId=${encodeURIComponent(machine.id)}`)
              }
            >
              Dati di produzione
            </Button>
          </>
        }
      />
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 3 }}
      >
        <Chip
          size="small"
          variant="outlined"
          label={
            {
              online: "Online",
              offline: "Offline",
              maintenance: "Manutenzione",
              error: "Errore",
            }[machine.status] || machine.status
          }
          color={getStatusColor(machine.status) as any}
        />
        <Typography variant="body2" color="text.secondary">
          Ultimo contatto:{" "}
          {machine.lastSeen
            ? new Date(machine.lastSeen).toLocaleString("it-IT")
            : "Mai ricevuto"}
        </Typography>
      </Stack>
      {notice && (
        <Alert
          severity={notice.severity}
          sx={{ mb: 2 }}
          onClose={() => setNotice(null)}
        >
          {notice.message}
        </Alert>
      )}
      <Tabs
        value={tab}
        onChange={(_, value) => {
          const params = new URLSearchParams(searchParams);
          params.set("tab", value);
          setSearchParams(params, { replace: true });
        }}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Sezioni della macchina"
        sx={{ mb: 3, borderBottom: "1px solid", borderColor: "divider" }}
      >
        {[
          ["overview", "Panoramica"],
          ["licenses", "Licenze e servizi"],
          ["remote", "Assistenza remota"],
        ].map(([value, label]) => (
          <Tab
            key={value}
            value={value}
            label={label}
            id={`machine-tab-${value}`}
            aria-controls={`machine-panel-${value}`}
          />
        ))}
      </Tabs>
      <Box
        role="tabpanel"
        id={`machine-panel-${tab}`}
        aria-labelledby={`machine-tab-${tab}`}
      >
        {tab === "overview" && (
          <Stack spacing={3}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "1.2fr 1fr" },
                gap: 3,
              }}
            >
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 3 }}>
                    Identità e assegnazione
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 3,
                    }}
                  >
                    {info("Cliente", machine.companyName)}
                    {info("Rivenditore", machine.resellerName || "Diretto")}
                    {info("Sede", machine.location)}
                    {info("Versione HMI", installedHmiBuild)}
                    {info("Matricola", machine.serialNumber)}
                  </Box>
                </CardContent>
              </Card>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 3 }}>
                    Specifiche tecniche
                  </Typography>
                  <Stack spacing={2.5}>
                    {info(
                      "Potenza laser",
                      machine.specifications?.power
                        ? `${machine.specifications.power.toLocaleString("it-IT")} W`
                        : "Non indicata",
                    )}
                    {info(
                      "Area di lavoro",
                      machine.specifications?.workArea
                        ? `${machine.specifications.workArea.width} × ${machine.specifications.workArea.height} mm`
                        : "Non indicata",
                    )}
                    {info(
                      "Velocità massima",
                      machine.specifications?.maxSpeed
                        ? `${machine.specifications.maxSpeed} mm/s`
                        : "Non indicata",
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Box>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                >
                  <Box>
                    <Typography variant="h6">
                      Lavorazioni della macchina
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      Consulta i pezzi e lo storico di taglio di questa
                      macchina.
                    </Typography>
                  </Box>
                  <Button
                    startIcon={<PlaylistPlay />}
                    variant="outlined"
                    onClick={() =>
                      navigate(
                        `/jobs?machineId=${encodeURIComponent(machine.id)}`,
                      )
                    }
                  >
                    Apri lavorazioni
                  </Button>
                </Stack>
                {machine.currentProduction && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" fontWeight={700}>
                      {machine.currentProduction.code}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ultimo stato ricevuto ·{" "}
                      {machine.currentProduction.partsCompleted} di{" "}
                      {machine.currentProduction.totalParts} pezzi completati
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={
                        machine.currentProduction.totalParts
                          ? Math.min(
                              100,
                              (machine.currentProduction.partsCompleted /
                                machine.currentProduction.totalParts) *
                                100,
                            )
                          : 0
                      }
                      sx={{ mt: 1.5, height: 5, borderRadius: 1 }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Stack>
        )}
        {tab === "licenses" && (
          <Stack spacing={3}>
            <Alert severity="info" variant="outlined">
              La licenza NextControl appartiene alla macchina. Le licenze di
              NextCut, NextFrame, NextDrill e NextCloud sono disponibili in Licenze
              software.
            </Alert>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  NextControl · diritto d’uso
                </Typography>
                {info(
                  "Scadenza",
                  machine.licenseExpirationDate
                    ? formatLicenseDate(machine.licenseExpirationDate)
                    : "Senza scadenza",
                )}
                {canManage && (
                  <Button
                    variant="outlined"
                    sx={{ mt: 3 }}
                    onClick={() => navigate("/machines/manage")}
                  >
                    Gestisci licenze della macchina
                  </Button>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3 }}>
                  Servizi e moduli aggiuntivi
                </Typography>
                <Stack spacing={2}>
                  {MACHINE_LICENSES.map(({ key, label }) => {
                    const licenseInfo = getLicenseInfo(machine, key);
                    return (
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        gap={2}
                        key={key}
                        sx={{
                          pb: 2,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {label}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={licenseInfo.color}
                          label={
                            licenseInfo.status === "DISABLED"
                              ? "Non incluso"
                              : licenseInfo.status === "EXPIRED"
                                ? "Scaduto"
                                : licenseInfo.expiresAt
                                  ? `Attivo fino al ${formatLicenseDate(licenseInfo.expiresAt)}`
                                  : "Attivo · permanente"
                          }
                        />
                      </Stack>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}
        {tab === "remote" && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Assistenza remota
              </Typography>
              <Alert
                severity={remoteConfig ? "info" : "warning"}
                sx={{ mb: 2 }}
              >
                {remoteConfig
                  ? `Server privato CM: ${remoteConfig.idServer}. Configura questi valori in RustDesk → Impostazioni → Rete, sia sull’HMI sia sul PC di assistenza, prima di connetterti.`
                  : "Configurazione del server privato non disponibile. Connessione disabilitata."}
              </Alert>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                  mb: 3,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    RustDesk ID
                  </Typography>
                  <Typography>{machine.rustdeskId || "-"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    RustDesk Password
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography>
                      {machine.rustdeskPassword
                        ? showRemotePassword
                          ? machine.rustdeskPassword
                          : "••••••••"
                        : "Non disponibile"}
                    </Typography>
                    {machine.rustdeskPassword && (
                      <IconButton
                        size="small"
                        aria-label={
                          showRemotePassword
                            ? "Nascondi password remota"
                            : "Mostra password remota"
                        }
                        onClick={() => setShowRemotePassword((v) => !v)}
                      >
                        {showRemotePassword ? (
                          <VisibilityOffOutlined fontSize="small" />
                        ) : (
                          <VisibilityOutlined fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Aggiornato RustDesk
                  </Typography>
                  <Typography>
                    {machine.rustdeskUpdatedAt
                      ? new Date(machine.rustdeskUpdatedAt).toLocaleString()
                      : "-"}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                <Button
                  variant="contained"
                  onClick={openRustDeskDesktop}
                  disabled={!machine.rustdeskId || !remoteConfig}
                  startIcon={<OpenInNew />}
                >
                  Connetti con RustDesk Desktop
                </Button>
                <Button
                  variant="outlined"
                  disabled={!remoteConfig}
                  onClick={() =>
                    remoteConfig &&
                    copyText(
                      `ID Server: ${remoteConfig.idServer}\nRelay Server: ${remoteConfig.relayServer}\nKey: ${remoteConfig.key}\nAPI Server: (vuoto)`,
                      "Configurazione server privato",
                    )
                  }
                  startIcon={<ContentCopy />}
                >
                  Copia configurazione server
                </Button>
                <Button
                  variant="text"
                  onClick={() =>
                    copyText(machine.rustdeskId || "", "RustDesk ID")
                  }
                  disabled={!machine.rustdeskId}
                  startIcon={<ContentCopy />}
                >
                  Copia ID
                </Button>
                <Button
                  variant="text"
                  onClick={() =>
                    copyText(
                      machine.rustdeskPassword || "",
                      "RustDesk Password",
                    )
                  }
                  disabled={!machine.rustdeskPassword}
                  startIcon={<ContentCopy />}
                >
                  Copia Password
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};
export default MachineDetailPage;
